/**
 * Inteligência de vendas para moda: regras PURAS (sem banco), usadas pela API, pelo
 * job noturno e pelas telas. As fórmulas estão explicadas para a lojista em
 * docs/INTELIGENCIA.md. Regras adaptadas da inteligência do Tirou (menu engineering):
 * mínimo de 5 pedidos, corte de popularidade 0,7/N, corte de margem = margem média e
 * score de ranking 60% lucro + 40% quantidade.
 */
import { addDaysKey } from './dates';

// ---------------------------------------------------------------------------
// Parâmetros
// ---------------------------------------------------------------------------

/** Mínimo de PEDIDOS distintos no período para o produto ganhar classe e score. */
export const INTELLIGENCE_MIN_ORDERS = 5;
/** Janelas oferecidas na matriz, no ranking e nas afinidades. */
export const INTELLIGENCE_PERIODS = [30, 60, 90] as const;
export type IntelligencePeriod = (typeof INTELLIGENCE_PERIODS)[number];
/** Popularidade alta = participação na quantidade >= 0,7 / N (N = produtos elegíveis). */
export const POPULARITY_FACTOR = 0.7;
/** Score do ranking: 60% do maior lucro, 40% da maior quantidade. */
export const RANKING_WEIGHTS = { profit: 0.6, quantity: 0.4 } as const;
/** Par "comprados juntos" precisa aparecer em pelo menos 2 pedidos. */
export const MIN_PAIR_ORDERS = 2;
/** Capital parado: peça com estoque e sem venda há 60 dias. */
export const IDLE_STOCK_DAYS = 60;
/** Faixas de cobertura de estoque (dias). */
export const COVERAGE_THRESHOLDS = { critical: 7, warning: 15, excess: 90 } as const;
export const DEFAULT_LEAD_TIME_DAYS = 7;
export const DEFAULT_TARGET_DAYS = 30;
/** Divisor mínimo da venda média: peça que acabou de chegar não parece vender demais. */
export const MIN_VELOCITY_DAYS = 7;
/** Variação de até 10% (para mais ou para menos) conta como estável. */
export const TREND_STABLE_PERCENT = 10;
/** Margem a até 0,1 ponto da média conta como "na média" (alta): evita separar produtos por arredondamento de centavos. */
export const MARGIN_TOLERANCE_POINTS = 0.1;

const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// Classes da matriz (nomes de varejo de moda)
// ---------------------------------------------------------------------------

export type ProductClassKey = 'estrela' | 'carro_chefe' | 'joia_escondida' | 'encalhe';
export const PRODUCT_CLASSES: ProductClassKey[] = ['estrela', 'carro_chefe', 'joia_escondida', 'encalhe'];

/** Por que um produto vendido ficou sem classe ("aguardando dados"). */
export type IneligibleReason = 'poucos_pedidos' | 'sem_custo';
/** No ranking também aparece quem não vendeu no período. */
export type RankingReason = IneligibleReason | 'sem_venda';

export type InsightTone = 'success' | 'warning' | 'info' | 'danger';

export const PRODUCT_CLASS_META: Record<
  ProductClassKey,
  { label: string; plural: string; summary: string; recommendation: string; tone: InsightTone }
> = {
  estrela: {
    label: 'Estrela',
    plural: 'Estrelas',
    summary: 'Vende muito e dá boa margem.',
    recommendation: 'Proteja o estoque para não faltar numeração e dê destaque na vitrine e no catálogo.',
    tone: 'success',
  },
  carro_chefe: {
    label: 'Carro-chefe',
    plural: 'Carros-chefe',
    summary: 'Vende muito, mas a margem é baixa.',
    recommendation:
      'Revise o custo com o fornecedor ou suba o preço aos poucos. Use a peça para atrair clientes e ofereça junto peças de margem maior.',
    tone: 'warning',
  },
  joia_escondida: {
    label: 'Joia escondida',
    plural: 'Joias escondidas',
    summary: 'Margem boa, mas vende pouco.',
    recommendation: 'Dê vitrine: fotos melhores, destaque no catálogo e combinações em looks com as Estrelas.',
    tone: 'info',
  },
  encalhe: {
    label: 'Encalhe',
    plural: 'Encalhes',
    summary: 'Vende pouco e com margem baixa.',
    recommendation: 'Faça promoção, monte kits ou leve para a liquidação. Se continuar parado, tire de linha.',
    tone: 'danger',
  },
};

export const RANKING_REASON_LABELS: Record<RankingReason, string> = {
  poucos_pedidos: 'Aguardando dados: menos de 5 pedidos no período',
  sem_custo: 'Aguardando dados: cadastre o custo',
  sem_venda: 'Sem venda no período',
};

/** Frase curta que a tela mostra no lugar da classe/score. */
export function describeReason(reason: RankingReason, orders: number, minOrders = INTELLIGENCE_MIN_ORDERS): string {
  switch (reason) {
    case 'poucos_pedidos':
      return `${orders} de ${minOrders} pedidos`;
    case 'sem_custo':
      return 'Sem custo cadastrado';
    case 'sem_venda':
      return 'Sem venda no período';
  }
}

// ---------------------------------------------------------------------------
// Margem de contribuição e classificação
// ---------------------------------------------------------------------------

/**
 * Margem de contribuição em centavos = receita líquida × (1 − encargos%) − custo.
 * Encargos = pricingChargesPercent(settings.pricing) (imposto + cartão + comissão + outros), limitado a 95%.
 */
export function contributionProfit(revenueCents: number, costCents: number, chargesPercent = 0): number {
  const charges = Math.min(95, Math.max(0, chargesPercent));
  return Math.round(revenueCents * (1 - charges / 100) - costCents);
}

export interface ClassifyInput {
  productId: string;
  /** Pedidos distintos com o produto no período. */
  orders: number;
  quantity: number;
  /** Receita líquida de descontos (rateio do desconto do pedido). */
  revenueCents: number;
  /** Custo total das peças vendidas; null = alguma peça sem custo conhecido. */
  costCents: number | null;
}

export interface ClassifiedProduct extends ClassifyInput {
  eligible: boolean;
  reason: IneligibleReason | null;
  /** Margem de contribuição em centavos (null sem custo). */
  profitCents: number | null;
  /** Margem de contribuição em % da receita (1 casa). */
  marginPercent: number | null;
  /** Participação na quantidade vendida pelos elegíveis (%, 1 casa). Null para não elegíveis. */
  sharePercent: number | null;
  class: ProductClassKey | null;
}

export interface ClassificationResult {
  items: ClassifiedProduct[];
  cutoffs: {
    eligibleCount: number;
    /** 0,7 / N em % (null sem elegíveis). */
    popularityPercent: number | null;
    /** Média simples da margem % dos elegíveis (null sem elegíveis). */
    marginPercent: number | null;
  };
}

/** Custo conhecido: existe e é maior que zero ("100% de margem" é a pior forma de dizer que o custo é desconhecido). */
export function isCostKnown(costCents: number | null | undefined): costCents is number {
  return typeof costCents === 'number' && Number.isFinite(costCents) && costCents > 0;
}

/**
 * Matriz de produtos (popularidade × margem), regras do Tirou:
 *  - elegível = pedidos >= mínimo E custo conhecido; senão motivo 'poucos_pedidos' ou 'sem_custo';
 *  - popularidade = quantidade / quantidade total dos elegíveis; alta se >= 0,7 / N;
 *  - margem alta se margem % >= média simples da margem % dos elegíveis (tolerância de 0,1 ponto).
 * Quem ficou de fora não entra no N, no total nem na média.
 */
export function classifyProducts(
  rows: ClassifyInput[],
  opts: { minOrders?: number; chargesPercent?: number } = {},
): ClassificationResult {
  const minOrders = Math.max(1, opts.minOrders ?? INTELLIGENCE_MIN_ORDERS);
  const charges = opts.chargesPercent ?? 0;

  const base = rows.map((r) => {
    const reason: IneligibleReason | null = r.orders < minOrders ? 'poucos_pedidos' : !isCostKnown(r.costCents) ? 'sem_custo' : null;
    const profit = isCostKnown(r.costCents) ? contributionProfit(r.revenueCents, r.costCents, charges) : null;
    const margin = profit !== null && r.revenueCents > 0 ? (profit / r.revenueCents) * 100 : null;
    return { row: r, reason, profit, margin };
  });

  const eligible = base.filter((b) => b.reason === null);
  const n = eligible.length;
  const totalQty = eligible.reduce((s, b) => s + Math.max(0, b.row.quantity), 0);
  const margins = eligible.map((b) => b.margin).filter((m): m is number => m !== null);
  const marginAvg = margins.length ? margins.reduce((s, m) => s + m, 0) / margins.length : null;
  const cutoff = n > 0 ? POPULARITY_FACTOR / n : null;

  const items: ClassifiedProduct[] = base.map((b) => {
    const isEligible = b.reason === null;
    const share = isEligible && totalQty > 0 ? Math.max(0, b.row.quantity) / totalQty : null;
    let cls: ProductClassKey | null = null;
    if (isEligible && cutoff !== null) {
      const highPop = (share ?? 0) >= cutoff;
      const highMargin = b.margin !== null && marginAvg !== null && b.margin >= marginAvg - MARGIN_TOLERANCE_POINTS;
      cls = highPop ? (highMargin ? 'estrela' : 'carro_chefe') : highMargin ? 'joia_escondida' : 'encalhe';
    }
    return {
      ...b.row,
      eligible: isEligible,
      reason: b.reason,
      profitCents: b.profit,
      marginPercent: b.margin === null ? null : round1(b.margin),
      sharePercent: share === null ? null : round1(share * 100),
      class: cls,
    };
  });

  return {
    items,
    cutoffs: {
      eligibleCount: n,
      popularityPercent: cutoff === null ? null : round2(cutoff * 100),
      marginPercent: marginAvg === null ? null : round1(marginAvg),
    },
  };
}

/**
 * Score 0..100 do ranking de lucro: 100 × (0,6 × lucro / maior lucro + 0,4 × quantidade / maior quantidade),
 * calculado só entre os elegíveis (os demais recebem null). Lucro negativo conta como zero.
 */
export function rankingScores(items: { eligible: boolean; profitCents: number | null; quantity: number }[]): (number | null)[] {
  const pool = items.filter((i) => i.eligible);
  const maxProfit = Math.max(0, ...pool.map((i) => Math.max(0, i.profitCents ?? 0)));
  const maxQty = Math.max(0, ...pool.map((i) => Math.max(0, i.quantity)));
  return items.map((i) => {
    if (!i.eligible) return null;
    const p = maxProfit > 0 ? Math.max(0, i.profitCents ?? 0) / maxProfit : 0;
    const q = maxQty > 0 ? Math.max(0, i.quantity) / maxQty : 0;
    return round1(100 * (RANKING_WEIGHTS.profit * p + RANKING_WEIGHTS.quantity * q));
  });
}

export type TrendDirection = 'up' | 'down' | 'stable' | 'new';

/** Tendência da quantidade contra o período anterior de mesmo tamanho. */
export function trendOf(current: number, previous: number): { percent: number | null; direction: TrendDirection } {
  if (previous <= 0) return { percent: null, direction: current > 0 ? 'new' : 'stable' };
  const percent = Math.round(((current - previous) / previous) * 100);
  if (Math.abs(percent) <= TREND_STABLE_PERCENT) return { percent, direction: 'stable' };
  return { percent, direction: percent > 0 ? 'up' : 'down' };
}

// ---------------------------------------------------------------------------
// Comprados juntos
// ---------------------------------------------------------------------------

/**
 * Métricas de um par A+B:
 *  - suporte = pedidos com A e B / total de pedidos;
 *  - confiança A→B = P(B|A) = pedidos com A e B / pedidos com A (e o inverso B→A);
 *  - lift = suporte / (P(A) × P(B)); acima de 1 = compram juntos mais do que o acaso explicaria.
 */
export function affinityMetrics(pairOrders: number, ordersWithA: number, ordersWithB: number, totalOrders: number) {
  const safe = (num: number, den: number) => (den > 0 ? num / den : 0);
  return {
    supportPercent: round2(safe(pairOrders, totalOrders) * 100),
    confidenceAB: round1(safe(pairOrders, ordersWithA) * 100),
    confidenceBA: round1(safe(pairOrders, ordersWithB) * 100),
    lift: round2(ordersWithA > 0 && ordersWithB > 0 ? (pairOrders * totalOrders) / (ordersWithA * ordersWithB) : 0),
  };
}

// ---------------------------------------------------------------------------
// Cobertura de estoque e reposição
// ---------------------------------------------------------------------------

export type CoverageStatus = 'ruptura' | 'critico' | 'atencao' | 'ok' | 'excesso';
export const COVERAGE_STATUSES: CoverageStatus[] = ['ruptura', 'critico', 'atencao', 'ok', 'excesso'];

export const COVERAGE_STATUS_META: Record<CoverageStatus, { label: string; description: string; tone: InsightTone }> = {
  ruptura: { label: 'Ruptura', description: 'Esgotado e com venda recente.', tone: 'danger' },
  critico: { label: 'Crítico', description: 'Acaba em até 7 dias.', tone: 'danger' },
  atencao: { label: 'Atenção', description: 'Acaba em até 15 dias.', tone: 'warning' },
  ok: { label: 'OK', description: 'Estoque para 16 a 90 dias.', tone: 'success' },
  excesso: { label: 'Excesso', description: 'Estoque para mais de 90 dias ou sem venda.', tone: 'info' },
};

/**
 * Venda média diária ignorando os dias em que a peça estava esgotada:
 * dias disponíveis = dias da janela − dias esgotada (inferido das movimentações), nunca menos que
 * os dias com venda nem que 7 dias (ou a janela, se menor).
 */
export function salesVelocity(input: { soldQty: number; windowDays: number; stockoutDays: number; saleDays: number }): {
  availableDays: number;
  dailyAvg: number;
} {
  const windowDays = Math.max(0, input.windowDays);
  const availableDays = Math.min(windowDays, Math.max(0, windowDays - Math.max(0, input.stockoutDays)));
  const divisor = Math.max(availableDays, input.saleDays, Math.min(MIN_VELOCITY_DAYS, windowDays));
  const dailyAvg = input.soldQty > 0 && divisor > 0 ? input.soldQty / divisor : 0;
  return { availableDays: round1(availableDays), dailyAvg };
}

/** Dias de cobertura = estoque / venda média diária (null sem venda). */
export function coverageDays(stock: number, dailyAvg: number): number | null {
  if (dailyAvg <= 0) return null;
  return Math.max(0, stock) / dailyAvg;
}

export function coverageStatus(stock: number, dailyAvg: number): CoverageStatus {
  if (stock <= 0) return 'ruptura';
  if (dailyAvg <= 0) return 'excesso';
  const days = stock / dailyAvg;
  if (days <= COVERAGE_THRESHOLDS.critical) return 'critico';
  if (days <= COVERAGE_THRESHOLDS.warning) return 'atencao';
  if (days > COVERAGE_THRESHOLDS.excess) return 'excesso';
  return 'ok';
}

/**
 * Quantidade sugerida de compra: cobrir o prazo de entrega do fornecedor e depois mais `targetDays`.
 * = arredonda para cima(venda média × (prazo + alvo) − estoque − já pedido ao fornecedor), mínimo 0.
 */
export function reorderQuantity(input: { dailyAvg: number; stock: number; onOrder?: number; leadTimeDays: number; targetDays: number }): number {
  if (input.dailyAvg <= 0) return 0;
  const need = input.dailyAvg * (Math.max(0, input.leadTimeDays) + Math.max(0, input.targetDays));
  const have = Math.max(0, input.stock) + Math.max(0, input.onOrder ?? 0);
  return Math.max(0, Math.ceil(need - have - 1e-9));
}

/** Data estimada em que o estoque acaba (dia local). Null sem venda ou já esgotado. */
export function stockoutDateKey(todayKey: string, stock: number, dailyAvg: number): string | null {
  const days = coverageDays(stock, dailyAvg);
  if (days === null || stock <= 0) return null;
  return addDaysKey(todayKey, Math.floor(days));
}

const URGENCY: Record<CoverageStatus, number> = { ruptura: 0, critico: 1, atencao: 2, ok: 3, excesso: 4 };

/** Ordem de urgência: ruptura (quem vendia mais primeiro), crítico, atenção, ok e excesso (menor cobertura primeiro). */
export function compareCoverageUrgency(
  a: { status: CoverageStatus; coverageDays: number | null; dailyAvg: number },
  b: { status: CoverageStatus; coverageDays: number | null; dailyAvg: number },
): number {
  if (a.status !== b.status) return URGENCY[a.status] - URGENCY[b.status];
  if (a.status === 'ruptura') return b.dailyAvg - a.dailyAvg;
  const ca = a.coverageDays ?? Number.POSITIVE_INFINITY;
  const cb = b.coverageDays ?? Number.POSITIVE_INFINITY;
  if (ca !== cb) return ca - cb;
  return b.dailyAvg - a.dailyAvg;
}

// ---------------------------------------------------------------------------
// Grade de tamanhos e cores
// ---------------------------------------------------------------------------

export const SIZE_ORDER = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'EG', 'EGG', 'G1', 'G2', 'G3', 'G4', 'U', 'UNICO', 'ÚNICO'];

export const isSizeOption = (name: string) => /^(tam|size|numera)/i.test(name.trim());
export const isColorOption = (name: string) => /^(cor|cores|color|colour)\b/i.test(name.trim());

const COLOR_WORDS: Record<string, string> = {
  preta: 'preto',
  branca: 'branco',
  vermelha: 'vermelho',
  amarela: 'amarelo',
  roxa: 'roxo',
  escura: 'escuro',
  clara: 'claro',
  dourada: 'dourado',
  prateada: 'prateado',
  estampada: 'estampado',
};

/** Junta "Preta" e "Preto" (concordância do nome da peça) na mesma cor: "Preto", "Azul Escuro". */
export function normalizeColorLabel(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLocaleLowerCase('pt-BR');
      const word = COLOR_WORDS[lower] ?? lower;
      return word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1);
    })
    .join(' ');
}

/** Valor de opção normalizado para agrupar: tamanho em maiúsculas, cor sem concordância, resto como veio. */
export function normalizeOptionValue(kind: 'size' | 'color' | 'other', value: string): string {
  const v = value.trim();
  if (kind === 'size') return v.toLocaleUpperCase('pt-BR');
  if (kind === 'color') return normalizeColorLabel(v);
  return v;
}

/** Ordena tamanhos: PP, P, M, G, GG... depois numéricos (34, 36...) e o resto em ordem alfabética. */
export function compareSizeLabels(a: string, b: string): number {
  const ua = a.trim().toUpperCase();
  const ub = b.trim().toUpperCase();
  const ia = SIZE_ORDER.indexOf(ua);
  const ib = SIZE_ORDER.indexOf(ub);
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  const na = Number(ua.replace(',', '.'));
  const nb = Number(ub.replace(',', '.'));
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return ua.localeCompare(ub, 'pt-BR');
}

/** Percentuais inteiros que somam 100 (maior resto). Pesos zerados ficam com 0. */
export function roundPercents(weights: number[]): number[] {
  return largestRemainder(weights, 100);
}

/** Distribui `total` unidades proporcionalmente aos pesos (método do maior resto). */
export function largestRemainder(weights: number[], total: number): number[] {
  const w = weights.map((x) => (Number.isFinite(x) && x > 0 ? x : 0));
  const sum = w.reduce((s, x) => s + x, 0);
  if (sum <= 0 || total <= 0) return w.map(() => 0);
  const quotas = w.map((x) => (x / sum) * total);
  const out = quotas.map((q) => Math.floor(q + 1e-9));
  let left = total - out.reduce((s, x) => s + x, 0);
  const order = quotas
    .map((q, i) => ({ i, frac: q - Math.floor(q + 1e-9), weight: w[i]! }))
    .filter((x) => x.weight > 0)
    .sort((a, b) => b.frac - a.frac || b.weight - a.weight || a.i - b.i);
  for (let k = 0; left > 0 && order.length; k = (k + 1) % order.length) {
    const idx = order[k]!.i;
    out[idx] = (out[idx] ?? 0) + 1;
    left--;
  }
  return out;
}

export interface PackLine {
  value: string;
  percent: number;
  qty: number;
}

/** Proporção de compra por tamanho (participação nas vendas) e quantidades num pacote de N peças. */
export function packProportion(sizes: { value: string; soldQty: number }[], packSize: number): PackLine[] {
  const weights = sizes.map((s) => Math.max(0, s.soldQty));
  const percents = roundPercents(weights);
  const qtys = largestRemainder(weights, Math.max(0, Math.floor(packSize)));
  return sizes.map((s, i) => ({ value: s.value, percent: percents[i]!, qty: qtys[i]! }));
}

/** "Compre nesta proporção: P 15%, M 35%, G 30% e GG 20%. Num pacote de 12 peças: 2 P, 4 M, 4 G e 2 GG." */
export function packSuggestionText(lines: PackLine[], packSize: number): string | null {
  const sold = lines.filter((l) => l.percent > 0);
  if (!sold.length) return null;
  const percents = joinPt(sold.map((l) => `${l.value} ${l.percent}%`));
  const pack = lines.filter((l) => l.qty > 0).map((l) => `${l.qty} ${l.value}`);
  const packText = pack.length ? ` Num pacote de ${packSize} peças: ${joinPt(pack)}.` : '';
  return `Compre nesta proporção: ${percents}.${packText}`;
}

/** "a, b e c" */
export function joinPt(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

// ---------------------------------------------------------------------------
// Formatação pt-BR dos destaques
// ---------------------------------------------------------------------------

/** 234000 -> "R$ 2.340" (reais inteiros, para frases curtas). */
export function formatMoneyShort(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0, minimumFractionDigits: 0 })
    .format(Math.round(cents / 100))
    .replace(/ /g, ' ');
}

/** 52.4 -> "52%"; com casas: 52,4% */
export function formatPercentBR(value: number, digits = 0): string {
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value)}%`;
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: Number.isInteger(value) ? 0 : 1 }).format(value);
}

/** "acaba hoje" | "acaba amanhã" | "acaba em 4 dias" */
export function stockoutPhrase(coverage: number): string {
  const days = Math.floor(Math.max(0, coverage));
  if (days <= 0) return 'acaba hoje';
  if (days === 1) return 'acaba amanhã';
  return `acaba em ${days} dias`;
}

/** Nome da peça com a variação ("Preta / M" vira "Legging Flare Preta M"). */
export function itemLabel(name: string, variantName: string | null | undefined): string {
  const v = (variantName ?? '').trim().replace(/\s*[/,]\s*/g, ' ');
  return v ? `${name} ${v}` : name;
}
